// URL encoding/decoding utilities for filter state management

/**
 * Encode filter object to URL query string
 * @param {Record<string, any>} filters - Filter object
 * @returns {string} - URL-encoded query string
 */
export function encodeFiltersToUrl(filters: Record<string, any>): string {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        // Handle array values (like CPV filters)
        value.forEach((item) => params.append(key, item))
      } else {
        params.set(key, value.toString())
      }
    }
  })

  return params.toString()
}

/**
 * Decode URL query string to filter object
 * @param {string} search - URL search string (e.g., "?key=value")
 * @param {Record<string, any>} defaultFilters - Default filter values
 * @returns {Record<string, any>} - Decoded filter object
 */
export function decodeFiltersFromUrl(
  search: string,
  defaultFilters: Record<string, any> = {},
): Record<string, any> {
  const params = new URLSearchParams(search)
  const filters: Record<string, any> = { ...defaultFilters }

  // Handle single-value parameters
  const singleValueKeys = [
    'order_object',
    'organization_name',
    'cpv_code',
    'organization_city',
    'organization_province',
    'client_type',
    'order_type',
    'tender_type',
    'procedure_result',
    'bzp_number',
    'date_from',
    'date_to',
    'territory_id',
  ]

  singleValueKeys.forEach((key) => {
    const value = params.get(key)
    if (value !== null) {
      if (key === 'territory_id') {
        filters[key] = parseInt(value, 10)
      } else if (key === 'is_tender_amount_below_eu') {
        filters[key] = value === 'true'
      } else {
        filters[key] = value
      }
    }
  })

  // Handle array parameters (CPV filters, notice types)
  const arrayKeys = ['cpv_filters', 'notice_types']
  arrayKeys.forEach((key) => {
    const values = params.getAll(key)
    if (values.length > 0) {
      filters[key.replace('_filters', '_filter')] = values
    }
  })

  return filters
}

/**
 * Get current URL with updated filters
 * @param {Record<string, any>} filters - Filter object to encode
 * @param {string} basePath - Base path (default: current path)
 * @returns {string} - Full URL with encoded filters
 */
export function getUrlWithFilters(
  filters: Record<string, any>,
  basePath: string = window.location.pathname,
): string {
  const queryString = encodeFiltersToUrl(filters)
  return queryString ? `${basePath}?${queryString}` : basePath
}

/**
 * Navigate to URL with filters (replaces current history entry)
 * @param {Record<string, any>} filters - Filter object
 * @param {string} basePath - Base path
 */
export function navigateWithFilters(filters: Record<string, any>, basePath: string): void {
  const url = getUrlWithFilters(filters, basePath)
  window.history.replaceState(null, '', url)
}

/**
 * Push new history entry with filters
 * @param {Record<string, any>} filters - Filter object
 * @param {string} basePath - Base path
 */
export function pushFiltersToHistory(filters: Record<string, any>, basePath: string): void {
  const url = getUrlWithFilters(filters, basePath)
  window.history.pushState(null, '', url)
}
